import { useContext, useEffect, useId, useMemo, useRef, useState } from "react";
import { Context } from "../../../context/Context";
import { fetchCopyRestaurantsAPI, fetchMenuAPI, fetchMenuAdditionsAPI, fetchMenusAPI, previewCopyAPI } from "../../../utils/api/menu";
import { Button, CircleX, ImagePlus, NoticeBox, Select } from "../../ui";
import { useModalLayer } from "../../modals/modalHooks";
import { LIFECYCLE_LABELS, localized, menuErrorMessage } from "../menuRole";
import { topActionsText } from "./topActionsText";

// ------------------------------------------------------------------ labels
export const bothNames = (value) => [value?.en, value?.ar].map((v) => String(v || "").trim()).filter(Boolean).filter((v, i, list) => list.indexOf(v) === i).join(" · ") || "—";
export const menuOptionLabel = (menu, lang) => `${menu.name} · ${LIFECYCLE_LABELS[lang]?.[menu.lifecycle] || menu.lifecycle}`;
export const sectionOptionLabel = (section, index) => `${index + 1}. ${bothNames(section.name)} (${(section.items || []).length})`;
export const itemOptionLabel = (item) => bothNames(item.name);

export const findSection = (menu, id) => (menu?.sections || []).find((s) => String(s._id) === String(id)) || null;
export const findItem = (section, id) => (section?.items || []).find((i) => String(i._id) === String(id)) || null;

// ------------------------------------------------------------ data loading
// Loads the full, current menu from the backend for the selected id (never
// the possibly stale copy on the page). `reloadKey` forces a refetch.
export function useMenuDetail(menuId, reloadKey = 0) {
  const { lang } = useContext(Context);
  const [state, setState] = useState({ menu: null, loading: false, error: "" });
  useEffect(() => {
    if (!menuId) {
      setState({ menu: null, loading: false, error: "" });
      return undefined;
    }
    let alive = true;
    setState((prev) => ({ menu: String(prev.menu?._id) === String(menuId) ? prev.menu : null, loading: true, error: "" }));
    fetchMenuAPI(menuId, { includeArchived: false })
      .then((menu) => alive && setState({ menu, loading: false, error: "" }))
      .catch((error) => alive && setState({ menu: null, loading: false, error: menuErrorMessage(error, lang) }));
    return () => {
      alive = false;
    };
  }, [menuId, reloadKey, lang]);
  return state;
}

// The restaurant's addition groups, for linking in the item form.
export function useRestaurantAdditions(restaurantId) {
  const [additions, setAdditions] = useState([]);
  useEffect(() => {
    if (!restaurantId) return undefined;
    let alive = true;
    fetchMenuAdditionsAPI(restaurantId)
      .then((list) => alive && setAdditions(list))
      .catch(() => alive && setAdditions([]));
    return () => {
      alive = false;
    };
  }, [restaurantId]);
  return additions;
}

// ------------------------------------------------ cross-restaurant copy
// Restaurants offered as copy source/destination. The list comes from the
// backend (GET menu/copy/restaurants), which applies the user's existing
// access scope; nothing is filtered here. The open restaurant is always an
// option (its menus were already loaded through the same authorization).
export function useCopyRestaurants(current, enabled = true) {
  const [state, setState] = useState({ list: [], loading: enabled });
  useEffect(() => {
    if (!enabled) return undefined;
    let alive = true;
    fetchCopyRestaurantsAPI()
      .then((list) => alive && setState({ list: Array.isArray(list) ? list : [], loading: false }))
      .catch(() => alive && setState({ list: [], loading: false }));
    return () => {
      alive = false;
    };
  }, [enabled]);
  const restaurants = useMemo(() => {
    const byId = new Map(state.list.map((r) => [String(r._id), r]));
    if (current?._id && !byId.has(String(current._id))) byId.set(String(current._id), current);
    return [...byId.values()];
  }, [state.list, current]);
  return { restaurants, loading: state.loading };
}

// Menus of one restaurant. The open restaurant's menus are already on the
// page; any other restaurant's are fetched (the backend checks access).
export function useRestaurantMenus(restaurantId, { currentRestaurantId, currentMenus, includeArchived = false }) {
  const isCurrent = !restaurantId || String(restaurantId) === String(currentRestaurantId);
  const [state, setState] = useState({ menus: [], loading: false });
  useEffect(() => {
    if (isCurrent) return undefined;
    let alive = true;
    setState({ menus: [], loading: true });
    fetchMenusAPI({ restaurant: restaurantId, includeArchived })
      .then((list) => alive && setState({ menus: (list || []).filter((m) => String(m.restaurant?._id) === String(restaurantId)), loading: false }))
      .catch(() => alive && setState({ menus: [], loading: false }));
    return () => {
      alive = false;
    };
  }, [restaurantId, isCurrent, includeArchived]);
  if (isCurrent) return { menus: currentMenus, loading: false };
  return state;
}

export function RestaurantPicker({ label, restaurants, loading, value, onChange, disabled }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  return (
    <Select
      label={label}
      placeholder={loading && !restaurants.length ? t.loading : t.selectRestaurant}
      value={value ? String(value) : ""}
      disabled={disabled || !restaurants.length}
      options={restaurants.map((r) => ({ value: String(r._id), label: r.businessName || r.id }))}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Backend dry run of a cross-restaurant Copy Item / Copy Section. Only runs
// when the request is complete and the restaurants differ.
export function useCopyPreview(request, enabled) {
  const { lang } = useContext(Context);
  const key = enabled && request ? JSON.stringify(request) : "";
  const [state, setState] = useState({ key: "", preview: null, loading: false, error: "" });
  useEffect(() => {
    if (!key) {
      setState({ key: "", preview: null, loading: false, error: "" });
      return undefined;
    }
    let alive = true;
    setState((prev) => ({ key, preview: prev.preview, loading: true, error: "" }));
    previewCopyAPI(JSON.parse(key))
      .then((preview) => alive && setState({ key, preview, loading: false, error: "" }))
      .catch((error) => alive && setState({ key, preview: null, loading: false, error: menuErrorMessage(error, lang) }));
    return () => {
      alive = false;
    };
  }, [key, lang]);
  const current = state.key === key;
  return { preview: current && !state.loading ? state.preview : null, loading: Boolean(key) && (!current || state.loading), error: current ? state.error : "" };
}

const NO_LINK = "__none";
const additionLabel = (addition) => (addition ? `${addition.shortName}${addition.title ? ` · ${bothNames(addition.title)}` : ""}` : "—");

// What happens to Restaurant-scoped links when content moves to another
// restaurant: each linked addition is mapped to one of the destination's
// additions (automatic match or the user's choice) or not linked; branch
// visibility is never carried over. `onMap(sourceId, destId|null)`.
export function CopyLinksPreview({ preview, loading, error, onMap, disabled }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  if (error) return <NoticeBox variant="danger" size="sm">{error}</NoticeBox>;
  if (loading || !preview) return <p className="menu-note">{t.checkingLinks}</p>;
  const mappable = preview.additionLinks.filter((link) => link.reason !== "unavailable");
  const options = [{ value: NO_LINK, label: t.doNotLink }, ...preview.destinationAdditions.map((a) => ({ value: String(a._id), label: additionLabel(a) }))];
  return (
    <section className="menu-copy-links" aria-label={t.crossRestaurantTitle}>
      <NoticeBox variant="warning" size="sm" title={t.crossRestaurantTitle}>
        {t.crossRestaurantBody(preview.source.restaurant.businessName, preview.destination.restaurant.businessName)}
      </NoticeBox>
      {preview.droppedBranchOverrides > 0 && <p className="menu-note">{t.branchOverridesDropped(preview.droppedBranchOverrides)}</p>}
      {preview.additionLinks.length === 0 ? (
        <p className="menu-note">{t.noAdditionLinks}</p>
      ) : (
        <>
          <h4 className="menu-action-step">{t.additionLinks}</h4>
          <p className="menu-note">{t.additionLinksHint}</p>
          <ul className="menu-copy-links-list">
            {mappable.map((link) => (
              <li key={String(link.sourceAddition._id)}>
                <Select
                  label={`${additionLabel(link.sourceAddition)} (${t.usedByItems(link.itemCount)})`}
                  value={link.destinationAddition ? String(link.destinationAddition._id) : NO_LINK}
                  options={options}
                  disabled={disabled}
                  onChange={(e) => onMap(String(link.sourceAddition._id), e.target.value === NO_LINK ? null : e.target.value)}
                />
                <span className={`menu-secondary ${link.status === "skipped" ? "is-warn" : ""}`}>{t.linkReasons[link.reason] || link.reason}</span>
              </li>
            ))}
          </ul>
          {preview.additionLinks.length > mappable.length && <p className="menu-note">{t.unavailableAdditions(preview.additionLinks.length - mappable.length)}</p>}
          {preview.skippedAdditionLinks > 0 && (
            <NoticeBox variant="neutral" size="sm">
              {t.skippedLinksSummary(preview.skippedAdditionLinks)}
            </NoticeBox>
          )}
        </>
      )}
    </section>
  );
}

// Result message for a finished copy (counts what was not linked).
export function copyResultMessage(t, base, result) {
  const cross = result?.crossRestaurant ? t.crossRestaurantDone : "";
  return `${base}${cross}${result?.droppedAdditionLinks ? t.droppedAdditions(result.droppedAdditionLinks) : ""}`;
}

// --------------------------------------------------------------- pickers
// Searchable single selects that cascade Restaurant -> Menu -> Section ->
// Item. Parents reset their children when they change (see the dialogs).
export function MenuPicker({ label, menus, value, onChange, disabled }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  return <Select label={label || t.menu} placeholder={t.selectMenu} value={value ? String(value) : ""} disabled={disabled} options={menus.map((m) => ({ value: String(m._id), label: menuOptionLabel(m, lang) }))} onChange={(e) => onChange(e.target.value)} />;
}

export function SectionPicker({ label, menu, loading, value, onChange, disabled }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const sections = menu?.sections || [];
  return (
    <Select
      label={label || t.section_}
      placeholder={loading ? t.loading : sections.length || !menu ? t.selectSection : t.noSections}
      value={value ? String(value) : ""}
      disabled={disabled || !menu || !sections.length}
      options={sections.map((s, i) => ({ value: String(s._id), label: sectionOptionLabel(s, i) }))}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

export function ItemPicker({ label, section, value, onChange, disabled }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const items = section?.items || [];
  return (
    <Select
      label={label || t.item_}
      placeholder={section && !items.length ? t.noItems : t.selectItem}
      value={value ? String(value) : ""}
      disabled={disabled || !section || !items.length}
      options={items.map((i) => ({ value: String(i._id), label: itemOptionLabel(i) }))}
      onChange={(e) => onChange(e.target.value)}
    />
  );
}

// Clears ids that are not (or no longer) valid for the loaded menu/section.
export function useDropInvalid(value, validIds, reset, ready) {
  useEffect(() => {
    if (ready && value && !validIds.includes(String(value))) reset();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ready, value, validIds.join()]);
}

// ------------------------------------------------------------ image field
export function ItemImageField({ current, file, onFile, disabled }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const inputId = useId();
  const [preview, setPreview] = useState("");
  useEffect(() => {
    if (!file) return setPreview("");
    const url = URL.createObjectURL?.(file) || "";
    setPreview(url);
    return () => url && URL.revokeObjectURL?.(url);
  }, [file]);
  const shown = preview || current;
  return (
    <div className="food-form-section menu-action-image">
      <h4>{t.newItemImage}</h4>
      <div className="menu-action-image-row">
        {shown ? <img src={shown} alt={current && !preview ? t.currentImage : ""} className="menu-action-image-preview" /> : null}
        <label className={`ui-button primary soft sm rounded menu-import-btn ${disabled ? "disabled" : ""}`} htmlFor={inputId}>
          <ImagePlus size={16} /> {current || file ? t.replaceImage : t.chooseImage}
          <input id={inputId} type="file" accept="image/jpeg,image/jpg,image/png,image/webp" disabled={disabled} aria-label={t.newItemImage} onChange={(e) => onFile(e.target.files?.[0] || null)} />
        </label>
        {file && <span className="menu-secondary">{file.name}</span>}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ dialog shell
export function ActionDialog({ title, wide = false, busy = false, error = "", onClose, footer, children }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  const ref = useRef(null);
  // Escape inside an open dropdown only closes the dropdown (it marks the
  // event as handled); otherwise it closes the dialog.
  useModalLayer(true, { containerRef: ref, onEscape: (event) => !event?.defaultPrevented && !busy && onClose() });
  return (
    <div className="food-modal-overlay menu-action-overlay">
      <div ref={ref} tabIndex={-1} className={`food-modal menu-action-dialog ${wide ? "food-item-form-modal is-wide" : ""}`} role="dialog" aria-modal="true" aria-label={title} aria-busy={busy || undefined} dir={lang === "ar" ? "rtl" : "ltr"}>
        <header className="menu-action-dialog-header">
          <h3>{title}</h3>
          <Button type="button" variant="warning" appearance="soft" size="sm" rounded icon={<CircleX size={17} />} aria-label={t.close} title={t.close} disabled={busy} onClick={onClose} />
        </header>
        <div className="menu-action-dialog-body">{children}</div>
        {error && (
          <p className="menu-error" role="alert">
            {error}
          </p>
        )}
        <div className="food-modal-actions menu-action-dialog-footer">{footer}</div>
      </div>
    </div>
  );
}

// Standard Save/Cancel footer; `onSubmit` is ignored while busy.
export function DialogFooter({ submitLabel, onSubmit, onCancel, busy, disabled, tone = "primary" }) {
  const { lang } = useContext(Context);
  const t = topActionsText(lang);
  return (
    <>
      <Button type="button" variant={tone} appearance="filled" fullWidth rounded loading={busy} disabled={disabled || busy} onClick={() => !busy && onSubmit()}>
        {submitLabel}
      </Button>
      <Button type="button" variant="warning" appearance="soft" fullWidth rounded disabled={busy} onClick={onCancel}>
        {t.cancel}
      </Button>
    </>
  );
}

export { localized };
